// Sprint 4b AC-30 (Farbe: Cream + Anthrazit + Deep Teal) + AC-31 (Persona: landit
// spricht selbst, ich-Voice, Brand-Glyph reused). Variante D aus Whiteboard.
// Sprint 4c-3 C.1c + C.3 — Shared CoachMarker + BeziehungsRing oben rechts.

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import CoachMarker from "@/components/CoachMarker";
import BeziehungsRing from "@/components/BeziehungsRing";
import { BRAND } from "@/config/brand";

// 5 Quick-Picks für Hero-Bereich (Sprint 4 Retention 6.3).
const QUICK_PICKS = [
  {
    label: "Partner will nicht zuhören",
    context: "privat",
    anliegen:
      "Mein Partner will mir nicht zuhören, wenn ich etwas Wichtiges sagen will.",
    reaktion:
      "Er/Sie blockt ab, sagt 'müssen wir mal sehen' und zieht sich zurück.",
  },
  {
    label: "Kollege soll Aufgabe übernehmen",
    context: "beruflich",
    anliegen:
      "Ich will, dass ein Kollege eine Aufgabe übernimmt, die uns beiden wichtig ist.",
    reaktion:
      "Er/Sie fragt nach Details, will wissen 'wer macht was bis wann', und zögert.",
  },
  {
    label: "Mitarbeiter zur Pünktlichkeit bringen",
    context: "beruflich",
    anliegen:
      "Ein Mitarbeiter kommt ständig zu spät und ich muss das ansprechen.",
    reaktion: "Er/Sie wird still, entschuldigt sich, ändert aber nichts.",
  },
  {
    label: "Familienkonflikt",
    context: "familie",
    anliegen: "Es gibt einen Konflikt in der Familie, den ich ansprechen muss.",
    reaktion:
      "Familienmitglied blockt ab, sagt 'lass uns später reden', oder wird emotional.",
  },
  {
    label: "Nachbar-Sache",
    context: "alltag",
    anliegen: "Es gibt eine Sache mit dem Nachbarn, die geklärt werden muss.",
    reaktion:
      "Nachbar wird defensiv, sagt 'das war nicht so gemeint', oder ignoriert es.",
  },
];

export function DashboardHero({ primaryFokustyp = null, anliegenUsedToday = null, anliegenLimitToday = null }) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus({ preventScroll: true });
  }, []);

  const startWithText = (anliegen, reaktion, context) => {
    const params = new URLSearchParams();
    if (anliegen) params.set("q", anliegen);
    if (reaktion) params.set("r", reaktion);
    if (context) params.set("c", context);
    const qs = params.toString();
    navigate(`/dashboard/anliegen${qs ? `?${qs}` : ""}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (trimmed.length < 3) {
      navigate("/dashboard/anliegen");
      return;
    }
    startWithText(trimmed, "", "");
  };

  return (
    <section
      data-testid="dashboard-hero"
      className="rounded-2xl bg-[#FAF7F2] border border-[#E8E2D6] p-7 sm:p-10 shadow-[0_4px_24px_-12px_rgba(15,118,110,0.18)]"
    >
      {/* Coach-Marker (oben links) + BeziehungsRing (oben rechts, nur wenn primary bekannt) */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <CoachMarker testid="dashboard-hero-coach-marker" />
        {primaryFokustyp && (
          <BeziehungsRing primarySlug={primaryFokustyp} size={80} />
        )}
      </div>

      {/* AC-31 — Headline mit „uns" (verbindet Coach + User) */}
      <h1
        data-testid="dashboard-hero-headline"
        className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#1F2937]"
      >
        Lass uns die richtigen Worte finden.
      </h1>
      <p
        data-testid="dashboard-hero-subtitle"
        className="mt-2 text-sm sm:text-base text-[#4B5563] max-w-2xl"
      >
        Beschreib mir kurz, was du klären willst — ich zeig dir, wie du das schaffst, das du dir vornimmst.
      </p>

      {/* Sprint 5b S.5b.2 + S.5b.3 — Free-Counter „X/3 Anliegen heute" + Upgrade-CTA bei Limit (3/3).
          Wird nur für Free-User gerendert (anliegenLimitToday !== null). */}
      {anliegenLimitToday !== null && (
        <div
          data-testid="dashboard-hero-counter"
          data-limit-reached={anliegenUsedToday >= anliegenLimitToday ? "true" : "false"}
          className={`mt-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg px-4 py-3 border ${
            anliegenUsedToday >= anliegenLimitToday
              ? "bg-amber-50 border-amber-300 text-amber-900"
              : "bg-stone-100 border-stone-200 text-stone-600"
          }`}
        >
          <span className="text-sm font-medium">
            Du hast heute{" "}
            <strong data-testid="dashboard-hero-counter-value">
              {anliegenUsedToday ?? 0}/{anliegenLimitToday}
            </strong>{" "}
            Flexpfade genutzt.
          </span>
          {anliegenUsedToday >= anliegenLimitToday && (
            <button
              type="button"
              data-testid="dashboard-hero-upgrade-cta"
              onClick={() => navigate("/abo")}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold rounded-md text-sm transition-colors whitespace-nowrap"
            >
              Mehr Flexpfade? → <span className="font-semibold">{BRAND.name}</span> Pro Solo
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        data-testid="dashboard-hero-form"
        className="mt-5 flex flex-col sm:flex-row gap-3"
      >
        <input
          ref={inputRef}
          type="text"
          data-testid="dashboard-hero-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="z.B. Mein Partner blockt ab, wenn ich etwas Wichtiges sagen will."
          className="flex-1 px-4 py-3 rounded-lg bg-white text-[#1F2937] placeholder:text-stone-400 border border-[#E8E2D6] focus:outline-none focus:border-[#0F766E] focus:ring-2 focus:ring-[#0F766E]/20 text-base transition-colors"
          maxLength={400}
        />
        <button
          type="submit"
          data-testid="dashboard-hero-submit"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#0F766E] hover:bg-[#0D5F58] text-white font-bold rounded-lg transition-colors whitespace-nowrap shadow-sm"
        >
          Lass uns loslegen <ArrowRight className="w-4 h-4" />
        </button>
      </form>

      {/* Quick-Picks */}
      <div className="mt-6">
        <p className="text-xs uppercase tracking-widest text-[#6B7280] mb-2">
          Oder starte direkt mit einem typischen Flexpfad:
        </p>
        <div
          data-testid="dashboard-hero-quickpicks"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
        >
          {QUICK_PICKS.map((pick) => (
            <button
              key={pick.label}
              type="button"
              data-testid={`dashboard-hero-quickpick-${pick.context}-${pick.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => startWithText(pick.anliegen, pick.reaktion, pick.context)}
              className="text-left px-3 py-2.5 rounded-lg bg-white hover:bg-[#0F766E]/5 border border-[#E8E2D6] hover:border-[#0F766E]/50 transition-all text-sm font-medium text-[#1F2937]"
            >
              {pick.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

export default DashboardHero;
